import React, { useState, useEffect, useRef } from "react";

const LogoLoader = ({ isLoading }) => {
  const [shouldRender, setShouldRender] = useState(true);
  const [isSliding, setIsSliding] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      setShouldRender(true);
      setIsSliding(false);
    } else {
      // Use rAF so the browser first commits translateY(0), THEN transitions to translateY(100%)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsSliding(true);
        });
      });
      timerRef.current = setTimeout(() => {
        setShouldRender(false);
      }, 1100);
    }
    return () => clearTimeout(timerRef.current);
  }, [isLoading]);

  if (!shouldRender) return null;

  const cyberPaths = [
    "M49.9,441.8c28.2,0,42.4,22.1,42.4,57.8v14.4h-34.3c-1.6-6.8-1.8-14.7-1.8-22.6v-16.9c0-5.2-2-8.6-6.1-8.6s-6.3,3.4-6.3,8.8v125.9c0,5.4,2.3,8.8,6.3,8.8s6.1-3.4,6.1-8.6v-18.1c0-7.9.2-15.8,1.8-22.6h34.3v15.6c0,35.7-14.2,57.8-42.4,57.8s-47.2-22.8-47.2-73.8v-44.2c0-51,18.3-73.8,47.2-73.8Z",
    "M175.8,444.1v2.3l-25.1,114.4v70.6h-36.8v-70.6l-25.1-114.4v-2.3h34.8l9.5,62.7h2.3l9.3-62.7h31.1Z",
    "M265.4,490.4v4.3c0,24.2-12.9,35.7-24.6,40.6v2.3c12.6,4.7,25.7,16,25.7,40.6v5c0,33.6-22.1,48.3-49,48.3h-36.3v-187.3h35.7c26.6,0,48.5,14.2,48.5,46.3ZM219.4,525.3c5.9,0,9.9-4.5,9.9-16.5v-24.8c0-12-4.1-16.5-9.9-16.5h-2.3v57.8h2.3ZM219.4,607.7c5.9,0,9.9-4.7,9.9-17.2v-24.4c0-12.6-4.1-17.2-9.9-17.2h-2.3v58.7h2.3Z",
    "M339.7,444.1v28.2h-30.7v50.3h26.4v28.2h-26.4v52.4h30.7v28.2h-67.3v-187.3h67.3Z",
    "M431.8,492.6v4.1c0,21.4-8.1,35.7-23,44.5v2.3c14,7.7,17.6,17.8,18.1,38.4l.5,18.1c.2,16.3,1.4,23.5,4.5,29.3v2.3h-34.1c-2.7-6.8-3.8-16.3-4.1-28.7l-.9-32.1c-.2-9.3-2.5-14.9-9.9-14.9v75.6h-35.9v-187.3h35.7c29.3,0,49.2,18.1,49.2,48.5ZM385.1,532.3c6.1,0,9.9-4.7,9.9-17.4v-30.2c0-12.4-3.8-17.2-9.9-17.2h-2.3v64.8h2.3Z"
  ];

  const spacePaths = [
    "M520.9,621h4.5c2.3,0,3.8-.9,3.8-2.7s-1.4-3.2-4.8-3.2c-5.7,0-13.5,3-21.4,7.6l-4.8,3c-8.4,5-16.1,10.6-26.9,10.6s-18.2-6.9-18.2-18.4,6.9-18.1,19.4-18.1h8.2v14.2c-3.1.7-6.3.8-9.5.8h-3.6c-2.2,0-3.7.9-3.7,2.7s1.4,3.2,4.8,3.2c5.2,0,11.3-2.8,19.1-7.4l4.8-3.1c8.5-5,18.1-10.7,29-10.7s18.3,6.8,18.3,18.4-6.9,18.1-19.6,18.1h-9v-14.2c3.1-.7,6.3-.8,9.5-.8Z",
    "M479.1,558.1h1.8c17,0,24.9,9.8,24.9,22.2h33.1v16.3h-84.8v-16.1c0-12.5,8-22.4,24.9-22.4ZM495.2,579.3c0-2.7-2-4.5-7.5-4.5h-15.4c-5.4,0-7.5,1.8-7.5,4.5v1h30.3v-1Z",
    "M454.2,528.7l83.8-7.7h1v15.9l-20.8,1.3v6.5l20.8,1.3v14.3h-1l-83.8-7.7v-24.1ZM505.3,539.1l-30.1,1.9v1l30.1,1.9v-4.9Z",
    "M453.2,499.5c0-12.8,10-19.2,26.2-19.2h6.5v15.5c-3.1.7-6.6.8-10.2.8h-7.7c-2.3,0-3.9.9-3.9,2.8s1.5,2.9,4,2.9h57c2.5,0,4-1,4-2.9s-1.5-2.8-3.9-2.8h-8.2c-3.6,0-7.2-.1-10.2-.8v-15.5h7c16.1,0,26.2,6.4,26.2,19.2s-10.3,21.4-33.4,21.4h-20c-23.1,0-33.4-8.3-33.4-21.4Z",
    "M454.2,446.7h12.8v13.9h22.8v-12h12.8v12h23.7v-13.9h12.8v30.4h-84.8v-30.4Z"
  ];

  const digitalPaths = [
    "M652.7,522.4v40.6c0,51.9-19,73.4-50.8,73.4h-34.3v-187.3h34.3c31.8,0,50.8,21.4,50.8,73.4ZM605.6,611.5c7.2,0,10.4-4.7,10.4-14.2v-110.6c0-9.5-3.2-14.2-10.4-14.2h-2v139h2Z",
    "M696.8,449v187.3h-36.6v-187.3h36.6Z",
    "M751.1,446.8c28.4,0,42.4,22.1,42.4,56.2v16h-34.1c-1.6-6.8-1.8-14.7-1.8-22.6v-16.9c0-5.2-2-8.6-6.1-8.6s-6.3,3.4-6.3,8.8v124.1c0,5.4,2,8.8,6.3,8.8s6.1-3.4,6.1-8.6v-56.2h35.9v88.5h-20.5l-4.7-16.5h-2.3c-2.3,9.5-7.9,18.3-23.2,18.3-22.3,0-38.4-21.9-38.4-67.5v-53.3c0-48.3,17.6-70.6,46.7-70.6Z",
    "M838.7,449v187.3h-36.6v-187.3h36.6Z",
    "M927,449v28.2h-22.6v159.1h-36.6v-159.1h-22.3v-28.2h81.5Z",
    "M989,449l16.9,185.1v2.3h-35.2l-2.9-46h-14.4l-2.9,46h-31.6v-2.3l16.9-185.1h53.3ZM966,561.9l-4.3-66.6h-2.3l-4.3,66.6h10.8Z",
    "M1048.6,449v159.1h28.7v28.2h-65.2v-187.3h36.6Z"
  ];

  return (
    <div
      className="fixed inset-0 z-[200] pointer-events-auto bg-black"
      style={{
        transition: 'transform 1s cubic-bezier(0.76, 0, 0.24, 1)',
        transform: isSliding ? 'translateY(100%)' : 'translateY(0)',
        willChange: 'transform',
      }}
    >
     
      {/* Logo Words */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4  pointer-events-none">
        <svg
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 380 1150 320"
          className="w-full max-w-screen-2xl"
        >
          <g>
            <defs>
              <style>{`
                .st0 { fill: #FFFFFF; }
                .logo-path-inner {
                  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.4));
                  opacity: 0;
                }
              `}</style>
            </defs>

            <g className="word-cyber">
              {cyberPaths.map((d, index) => (
                <path
                  key={`cyber-${index}`}
                  className="st0 logo-path logo-path-inner"
                  style={{
                    animation: "fadeInUpBounce 0.7s cubic-bezier(0.16, 1, 0.3, 1) both",
                    animationDelay: "0.2s"
                  }}
                  d={d}
                />
              ))}
            </g>

            <g className="word-space">
              {spacePaths.map((d, index) => (
                <path
                  key={`space-${index}`}
                  className="st0 logo-path logo-path-inner"
                  style={{
                    animation: "fadeInUpBounce 0.7s cubic-bezier(0.16, 1, 0.3, 1) both",
                    animationDelay: "0.8s"
                  }}
                  d={d}
                />
              ))}
            </g>

            <g className="word-digital">
              {digitalPaths.map((d, index) => (
                <path
                  key={`digital-${index}`}
                  className="st0 logo-path logo-path-inner"
                  style={{
                    animation: "fadeInUpBounce 0.7s cubic-bezier(0.16, 1, 0.3, 1) both",
                    animationDelay: "1.4s"
                  }}
                  d={d}
                />
              ))}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default LogoLoader;
